import { supabase } from '@/integrations/supabase/client';

export interface CoachingGoal {
  id: string;
  coach_id: string;
  team_id?: number;
  goal_title: string;
  goal_description?: string;
  target_value?: number;
  current_value: number;
  goal_type: 'attendance' | 'performance' | 'satisfaction' | 'skill_development' | 'wins';
  target_date?: string;
  status: 'active' | 'completed' | 'paused';
  created_at?: string;
  updated_at?: string;
}

export const coachServiceFixed = {
  // Helper function to resolve the correct coach ID
  async resolveCoachId(inputCoachId: string): Promise<string> {
    console.log('🎯 Resolving coach ID for:', inputCoachId);
    
    // First, check if the input coach ID exists in goals table
    const { data: goalsWithCoachId } = await supabase
      .from('coaching_goals')
      .select('coach_id')
      .eq('coach_id', inputCoachId)
      .limit(1);
    
    if (goalsWithCoachId && goalsWithCoachId.length > 0) {
      console.log('🎯 Found matching coach ID in goals:', inputCoachId);
      return inputCoachId;
    }
    
    // If no match, get any existing coach ID from goals table
    const { data: anyGoals } = await supabase
      .from('coaching_goals')
      .select('coach_id')
      .limit(1);
    
    if (anyGoals && anyGoals.length > 0) {
      console.log('🎯 Using fallback coach ID from goals:', anyGoals[0].coach_id);
      return anyGoals[0].coach_id;
    }
    
    console.log('🎯 No goals found, using original coach ID:', inputCoachId);
    return inputCoachId;
  },

  // Get coaching goals with calculated progress from RPC
  async getCoachingGoalsWithProgress(coachId: string): Promise<any[]> {
    console.log('🎯 Fetching coaching goals with progress for coach:', coachId);
    
    try {
      const actualCoachId = await this.resolveCoachId(coachId);
      console.log('🎯 Using resolved coach ID:', actualCoachId);
      
      const { data, error } = await supabase.rpc('get_coaching_goals_with_progress', {
        p_coach_id: actualCoachId
      });

      console.log('🎯 RPC Response - Error:', error);
      console.log('🎯 RPC Response - Data:', data);

      if (error) {
        console.error('❌ Error fetching coaching goals:', error);
        throw error;
      }

      // The RPC returns an object with goals and analytics
      const goals = (data as any)?.goals || [];
      console.log('🎯 Parsed goals from RPC:', goals);
      
      return goals;
    } catch (error) {
      console.error('❌ Error in getCoachingGoalsWithProgress:', error);
      throw error;
    }
  },

  // Create coaching goal
  async createCoachingGoal(goal: Omit<CoachingGoal, 'id'>): Promise<CoachingGoal> {
    const { data, error } = await supabase
      .from('coaching_goals')
      .insert([goal])
      .select()
      .single();

    if (error) {
      console.error('Error creating coaching goal:', error);
      throw error;
    }

    return data as CoachingGoal;
  }
};